/**
 * see {@tutorial step-by-step-style-corner}
 * @category Corners
 * @module SyleCorner
 */
import { helper, hfInstance } from "../Cells";
import { CORNER_TYPES } from "./constants";

import * as CornerTypes from "../types/CornerTypes";
import * as VuexTypes from "../types/VuexTypes";

const requiredStyleSettingHeaders = [
  "area",
  "rowpointer",
  "columnpointer",
  "rowmatch",
  "columnmatch",
  "style",
  "protection",
];
const requiredStyleAreaHeaders = ["id", "active", "ignorezero"];
/**
 * Style corner requires data in the format of { Change }
 * because each column key pointer is matched with all row key pointers,
 * and each row key pointer is matched with all column key pointers.
 * Therefore, only the indexes of row and column changes are needed.
 *
 * @param {CornerTypes.KeyPointerChangeMap} data - The change map containing key pointer mappings.
 * @returns {CornerTypes.Change} The resulting change after processing the key pointers.
 */
export function combineChangedRowsAndColumns(data) {
  /**@type {CornerTypes.Change} */
  const result = {
    row: [],
    column: [],
  };
  // Loop through the Key Pointers ('Data', 'Test', etc.)
  for (let key in data) {
    // Loop through each Index (e.g., Data[1], Test[0], etc.)
    for (let index in data[key]) {
      // Get the current object (e.g., { rows: Array(1971), columns: Array(5) })
      let item = data[key][index];
      result.row.push(...item.row);
      result.column.push(...item.column);
    }
  }
  return result;
}
const initialState = {
  styleSettings: {},
  areas: {},
  corners: {},
};
export default {
  namespaced: true,
  state: () => ({ ...initialState }),
  mutations: {},
  actions: {
    /**
     * @function
     * @name module:SyleCorner.startStyles
     * @description  Runs the style corner with all the data.
     * This is used when a sheet is opened, as the style settings table or style areas may change
     * and the updated styles need to be applied.
     *
     * @param {VuexTypes.VuexContext} context - The Vuex action context object.
     */
    startStyles({ commit, state, rootState, dispatch, getters, rootGetters }) {
      const currentSheetId = rootState.sheets.currentSheet.sheetId;
      /**@type {CornerTypes.Corners} */
      const corners = rootState.corners.corners;
      Object.entries(corners).forEach(([styleArea, value]) => {
        const { sheetHfId, columnRange, rowRange } = value;
        if (value.cornerType !== CORNER_TYPES.STYLE || currentSheetId !== sheetHfId) return;
        const rowLength = rowRange[3] - rowRange[1] + 1;
        const columnLength = rowRange[2] - rowRange[0] + 1;
        const row = new Array(rowLength).fill(0).map((item, index) => rowRange[1] + index);
        const column = new Array(columnLength).fill(0).map((item, index) => columnRange[1] + index);
        dispatch("_applyStyles", { styleArea, data: { row, column } });
      });
      dispatch("rangeStyle/persist", null, { root: true });
    },
    /**
     * @function
     * @name module:SyleCorner.applyStyles
     * @description Applies styles based on the provided data.
     *
     * This action is used to apply styles to a specific style area with the given change data.
     *
     * @param {VuexTypes.VuexContext} context - The Vuex action context object.
     * @param {Object} param1 - The parameters for applying styles.
     * @param {string} param1.styleArea - The area where the style is being applied.
     * @param {CornerTypes.KeyPointerChangeMap} param1.data - The indices of rows and columns that were changed.
     */
    applyStyles({ commit, state, rootState, dispatch, getters, rootGetters }, { styleArea, data }) {
      dispatch("_applyStyles", { styleArea, data: combineChangedRowsAndColumns(data) });
    },
    /**
     * @function
     * @name module:SyleCorner._applyStyles
     * @description like applyStyles but receive Change Type for data
     *
     * @param {VuexTypes.VuexContext} context - The Vuex action context object.
     * @param {Object} param1 - The parameters for applying styles.
     * @param {string} param1.styleArea - The area where the style is being applied.
     * @param {CornerTypes.Change} param1.data - The indices of rows and columns that were changed.
     */
    _applyStyles({ commit, state, rootState, dispatch, getters, rootGetters }, { styleArea, data }) {
      /** @type {CornerTypes.Corner} */
      const corner = rootState.corners.corners[styleArea];
      const sheetHfId = corner.sheetHfId;
      const currentSheetId = rootState.sheets.currentSheet.sheetId;
      Object.entries(corner.columnKeyPointers).forEach(([columnKeyPointer, columnKeyPointerMetaData]) => {
        Object.entries(corner.rowKeyPointers).forEach(([rowKeyPointer, rowKeyPointerMetaData]) => {
          const { range: columnRange, keyPointer: columnPointer } = columnKeyPointerMetaData[0];
          const { range: rowRange, keyPointer: rowPointer } = rowKeyPointerMetaData[0];

          const styles = rootState.preferences.allPreferences.styles;
          if (sheetHfId !== currentSheetId) return;
          const newStyles = [];
          data.row.forEach((rowIndex) => {
            if (rowIndex <= corner.columnRange[3]) return;
            const rowData = hfInstance.getCellValue({
              sheet: sheetHfId,
              col: columnRange[0],
              row: rowIndex,
            });

            for (let i = rowRange[0]; i <= rowRange[2]; i++) {
              const columnData = hfInstance.getCellValue({
                sheet: sheetHfId,
                col: i,
                row: rowRange[1],
              });
              if (!getters.isValidWithIgnoreZero(styleArea, columnData)) continue;
              const matchedData =
                state.styleSettings[
                  `${styleArea.toLowerCase()}|${rowPointer.toLowerCase()}|${columnPointer.toLowerCase()}|${String(
                    rowData
                  ).toLowerCase()}|${String(columnData).toLowerCase()}`
                ] ||
                state.styleSettings[
                  `*|${rowPointer.toLowerCase()}|${columnPointer.toLowerCase()}|${String(
                    rowData
                  ).toLowerCase()}|${String(columnData).toLowerCase()}`
                ];
              if (!matchedData) continue;
              const newRange = [i, rowIndex, i, rowIndex];
              const style = styles.find((item) => String(item.name) === String(matchedData.style));
              if (!style) continue;
              newStyles.push({ range: newRange, data: style });
            }
          });
          data.column.forEach((columnIndex) => {
            if (columnIndex <= corner.rowRange[2]) return;
            const columnData = hfInstance.getCellValue({
              sheet: sheetHfId,
              col: columnIndex,
              row: rowRange[1],
            });

            for (let i = columnRange[1]; i <= columnRange[3]; i++) {
              const rowData = hfInstance.getCellValue({
                sheet: sheetHfId,
                col: columnRange[0],
                row: i,
              });
              if (!getters.isValidWithIgnoreZero(styleArea, rowData)) continue;
              const matchedData =
                state.styleSettings[
                  `${styleArea.toLowerCase()}|${rowPointer.toLowerCase()}|${columnPointer.toLowerCase()}|${String(
                    rowData
                  ).toLowerCase()}|${String(columnData).toLowerCase()}`
                ] ||
                state.styleSettings[
                  `*|${rowPointer.toLowerCase()}|${columnPointer.toLowerCase()}|${String(
                    rowData
                  ).toLowerCase()}|${String(columnData).toLowerCase()}`
                ];
              if (!matchedData) continue;
              const newRange = [columnIndex, i, columnIndex, i];
              const style = styles.find((item) => String(item.name) === String(matchedData.style));
              if (!style) continue;
              newStyles.push({ range: newRange, data: style });
            }
          });

          newStyles.forEach((style, index) => {
            helper.style.main.add(style.data, style.range);
          });
        });
      });
      dispatch("rangeStyle/persist", null, { root: true });
      dispatch("cells/toggleRedrawCanvas", null, { root: true });
    },

    /**
     * @function
     * @name module:SyleCorner.loadStyleAreas
     * @description Loads the style area table.
     * The style area includes the following columns:
     * - `id`: The identifier for the style area. (style name also called style area)
     * - `active`: A flag indicating whether the style area is active.
     * - `ignoreZero`: A setting that determines whether zero values are ignored.
     *
     * @param {VuexTypes.VuexContext} context - The Vuex action context object.
     */
    loadStyleAreas({ commit, dispatch, state, rootState, getters, rootGetters }) {
      const isStyleAreaTableValid = rootGetters["corners/isTableValid"]("StyleAreas", requiredStyleAreaHeaders);
      if (!isStyleAreaTableValid) return;

      const headers = rootGetters["corners/getNamedExpressionAsArray"]("StyleAreas..header")[0];

      const headerMap = {};

      headers.forEach((item, index) => {
        headerMap[String(item).toLowerCase()] = index;
      });
      state.areas = {};
      const data = rootGetters["corners/getNamedExpressionAsArray"]("StyleAreas..data");
      data.forEach((row) => {
        const id = row[headerMap["id"]];
        const active = row[headerMap["active"]];
        const ignoreZero = row[headerMap["ignorezero"]];
        if (!id || !active) return;
        state.areas[String(id).toLowerCase()] = {
          active,
          ignoreZero,
        };
      });
    },
    /**
     * @function
     * @name module:SyleCorner.loadStyleSettings
     * @description Loads the style settings table.
     * The style settings table includes the following columns:
     * - `area`: The identifier for the style area (also called `styleName` or `styleId`).
     *    - A value of `*` applies to all style areas.
     * - `rowPointer`: The key pointer identifier for the row (e.g., "Data", "Title").
     * - `columnPointer`: The key pointer identifier for the column (e.g., "Data", "Title").
     * - `rowMatch`: The value to be matched in the row, typically a number.
     * - `columnMatch`: The value to be matched in the column, typically a number.
     * - `style`: The name of the style to be applied when both row and column match.
     * - `protection`: Indicates whether the cell should be protected or not (currently not implemented).
     *
     * @param {VuexTypes.VuexContext} context - The Vuex action context object.
     */
    loadStyleSettings({ commit, dispatch, state, rootState, getters, rootGetters }) {
      const isStyleSettingTableValid = rootGetters["corners/isTableValid"](
        "stylesettings",
        requiredStyleSettingHeaders
      );
      if (!isStyleSettingTableValid) return;

      const headers = rootGetters["corners/getNamedExpressionAsArray"]("stylesettings..header")[0];
      const data = rootGetters["corners/getNamedExpressionAsArray"]("stylesettings..data");
      const headerMap = {};

      headers.forEach((item, index) => {
        headerMap[item.toLowerCase()] = index;
      });

      data.forEach((row) => {
        const area = row[headerMap["area"]];
        const rowPointer = row[headerMap["rowpointer"]];
        const columnPointer = row[headerMap["columnpointer"]];
        const rowMatch = row[headerMap["rowmatch"]];
        const columnMatch = row[headerMap["columnmatch"]];
        const style = row[headerMap["style"]];
        const protection = row[headerMap["protection"]];
        if (
          !area ||
          !rowPointer ||
          !columnPointer ||
          !getters.isValidWithIgnoreZero(area, rowMatch) ||
          !getters.isValidWithIgnoreZero(area, columnMatch)
        )
          return;

        state.styleSettings[
          `${area.toLowerCase()}|${rowPointer.toLowerCase()}|${columnPointer.toLowerCase()}|${rowMatch}|${columnMatch}`
        ] = {
          style,
          protection,
        };
      });
    },
    async resetState({ commit, state, rootState, dispatch }) {
      Object.assign(state, { ...initialState });
    },
  },
  getters: {
    /**
     * @callback StyleAreaNamesResolver
     * @returns {Set<string>} A set of active style area names in lowercase.
     */

    /**
     * @function
     * @name module:SyleCorner.activeStyleAreaNames
     * @description Returns a set of all active style corner names from the current sheet.
     *
     * @returns {StyleAreaNamesResolver} A function that returns a set of active style area names in lowercase.
     */
    activeStyleAreaNames: (state, getters, rootState) => () => {
      return new Set(
        Object.keys(state.areas)
          .filter((item) => state.areas[item].active)
          .map((item) => item.toLowerCase())
      );
    },
    /**
     * @callback StyleAreaValidator
     * @param {string} styleArea - The style area name to validate against.
     * @param {number} value - The value to validate.
     * @returns {boolean} True if valid, otherwise false.
     */

    /**
     * @function
     * @name module:SyleCorner.isValidWithIgnoreZero
     * @description Checks if a value is valid for a given style area,
     * considering the `ignoreZero` setting.
     * Zero is considered valid if `ignoreZero` is off.
     *
     * @returns {StyleAreaValidator} A function that takes a style area and value to check if it's valid.
     */
    isValidWithIgnoreZero: (state, getters, rootState) => (styleArea, value) => {
      const areaKey = styleArea?.toLowerCase();
      const ignoreZero = state.areas[areaKey]?.ignoreZero;

      return Boolean(value) || (value === 0 && ignoreZero === 0);
    },
  },
};
